import { isEmpty } from 'lodash';
import { isNil, toString } from 'ramda';
import { format as asTagString } from '~/util/Validation/Tag';
import { KenmerkLookup } from '../../types';
import { assertNeverWithoutThrowing } from '~/util/assertion';
import type {
  AppStatus_Realworks,
  AppStatus_Realworks_FullExcludingKenmerkenSync__KenmerkMapping,
  AppStatus_Realworks_FullSync__KenmerkMapping,
  AppStatus_Realworks_LimitToKenmerkenSync__KenmerkMapping,
  RealworksKenmerk,
} from '~/graphql/types';

const text = {
  defaultGroupDescription: 'Groep zonder omschrijving',
};

type Params = {
  /** Our mapping */
  kenmerkMapping:
    | Array<
        | AppStatus_Realworks_FullSync__KenmerkMapping
        | AppStatus_Realworks_LimitToKenmerkenSync__KenmerkMapping
        | AppStatus_Realworks_FullExcludingKenmerkenSync__KenmerkMapping
      >
    | undefined;

  /** Coming from Realworks */
  realworksKenmerken: Array<RealworksKenmerk>;
};

/**
 * Creates a KenmerkLookup shape from the backend Realworks kenmerken and kenmerkMapping
 */
const createKenmerkLookup = ({
  kenmerkMapping,
  realworksKenmerken,
}: Params): KenmerkLookup | null => {
  if (isEmpty(realworksKenmerken) || isNil(kenmerkMapping)) return null;

  const savedMapping = kenmerkMapping.reduce(
    (prev, kenmerkMapping) => {
      prev[toString(kenmerkMapping.realworksKenmerkId)] = kenmerkMapping;
      return prev;
    },
    {} as Record<
      string,
      AppStatus_Realworks['relatieSync']['syncMetadata']['kenmerkMapping'][number]
    >,
  );

  /**
   * Merge Realworks kenmerken with saved mappings. It could be that Realworks kenmerken got updated in Realworks,
   * so we want to show these new kenmerken in the UI.
   */
  return realworksKenmerken.reduce((prev, rwKenmerk) => {
    const foundMapping = savedMapping[toString(rwKenmerk?.realworksKenmerkId)];

    const checked = (() => {
      // When relatieSync is initially returned, we don't have any kenmerkMapping yet.
      // In this case, we return false for all kenmerken.
      if (!foundMapping) return false;

      switch (foundMapping.__typename) {
        case 'AppStatus_Realworks_LimitToKenmerkenSync__KenmerkMapping':
          return foundMapping.include;
        case 'AppStatus_Realworks_FullExcludingKenmerkenSync__KenmerkMapping':
          return foundMapping.exclude;
        case 'AppStatus_Realworks_FullSync__KenmerkMapping':
          return true;
        default:
          assertNeverWithoutThrowing(foundMapping, 'createKenmerkLookup');
          return false;
      }
    })();

    const description =
      rwKenmerk?.realworksDescription ?? `${rwKenmerk.realworksKenmerkId}`;

    const tag: string | null = (() => {
      if (!foundMapping) return asTagString(description);
      return foundMapping.tag ?? null;
    })();

    const groupName = rwKenmerk?.realworksGroup ?? text.defaultGroupDescription;

    // We want to return these fields even if the mapping is not found so that the user can make the initial selection
    prev[groupName] = [
      ...(prev[groupName] || []),
      {
        id: rwKenmerk.realworksKenmerkId,
        description,
        group: groupName,
        tag,
        checked,
      },
    ];

    return prev;
  }, {} as KenmerkLookup);
};

export default createKenmerkLookup;
